import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  GraduationCap, Clock, CheckCircle2, Lock, PlayCircle, Loader2, FileText,
  ShieldCheck, Camera, BarChart3, CalendarDays, ChevronRight, BookOpen, Award, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";
import ModuloPlayer from "./ModuloPlayer";
import EsameFinaleSection from "./EsameFinaleSection";
import TimelineFormazione from "../components/TimelineFormazione";
import StreakCard from "../components/StreakCard";
import DtsCertificatoUpload from "../components/DtsCertificatoUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const STATO_UI = {
  bloccato: { label: "🔒 Bloccato", cls: "bg-slate-100 text-slate-500" },
  da_iniziare: { label: "▶️ Da iniziare", cls: "bg-blue-50 text-blue-700" },
  in_corso: { label: "🟡 In corso", cls: "bg-amber-50 text-amber-700" },
  completato: { label: "✅ Completato", cls: "bg-emerald-50 text-emerald-700" },
};

function fmtMin(sec) {
  return `${Math.round(sec / 60)} min`;
}
function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—";
}

/**
 * Animazione confetti di celebrazione al completamento del percorso.
 * Import dinamico per non appesantire il bundle iniziale.
 */
async function fireConfettiCelebration() {
  try {
    const { default: confetti } = await import("canvas-confetti");
    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ["#f59e0b", "#0a1f55", "#fbbf24", "#10b981", "#ffffff"];
    // Burst iniziale centrale ampio
    confetti({
      particleCount: 120,
      spread: 90,
      origin: { y: 0.5 },
      colors,
      zIndex: 9999,
    });
    // Cannoni laterali continui
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors,
        zIndex: 9999,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors,
        zIndex: 9999,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  } catch { /* canvas-confetti non disponibile */ }
}

export default function FormazioneACDT({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeModulo, setActiveModulo] = useState(null);

  const PACCHETTI_CON_CORSO = ["solo-corso", "corso-tessera", "corso-tessera-registro", "premium", "Premium"];
  const hasCorsoAccess =
    user?.role === "admin"
    || !!user?.ruolo_direttivo
    || !!user?.corso_sbloccato_dts
    || !!user?.corso_sbloccato_legacy
    || PACCHETTI_CON_CORSO.includes(user?.pacchetto);

  const load = useCallback(() => {
    if (!hasCorsoAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API}/api/formazione/overview`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Errore di caricamento"))
      .finally(() => setLoading(false));
  }, [hasCorsoAccess]);
  useEffect(() => { load(); }, [load]);

  if (activeModulo) {
    return (
      <ModuloPlayer
        modulo={activeModulo}
        tolleranza={data?.tolleranza_assenza_sec ?? 15}
        onBack={() => { setActiveModulo(null); load(); }}
        onModuleUpdate={load}
      />
    );
  }

  // GATE: se l'utente non ha accesso al corso, mostra opzioni sblocco (upload DTS + acquisto pacchetto)
  if (!hasCorsoAccess) {
    return <CorsoLockedGate user={user} onUnlocked={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-5">
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#102a6b]" /></div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 text-sm" data-testid="formazione-error">{error}</div>
      ) : (!data.privacy_accettata || !data.iscritto) ? (
        <InformativaFormazione onAccepted={load} version={data.privacy_version} />
      ) : (
        <Overview data={data} user={user} onOpenModulo={setActiveModulo} onRefresh={load} />
      )}
    </div>
  );
}

function CorsoLockedGate({ user, onUnlocked }) {
  return (
    <div className="space-y-6 max-w-4xl" data-testid="corso-locked-gate">
      <div className="rounded-3xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-8 md:p-10 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1020] text-[11px] font-extrabold uppercase tracking-[0.18em]">
            <Lock className="h-3 w-3" />
            Corso non ancora sbloccato
          </div>
          <h2 className="mt-3 text-[24px] md:text-[28px] font-extrabold leading-tight">
            Sblocca il Corso A.C.D.T. per la qualificazione professionale
          </h2>
          <p className="mt-2 text-white/85 text-[14px] md:text-[15px] leading-relaxed max-w-2xl">
            Hai <strong>due opzioni</strong>: acquista uno dei pacchetti ufficiali (dal Corso Solo € 99 al pacchetto completo con Registro € 218,90),
            oppure <strong>carica il tuo attestato Digital Tech Solutions</strong> se hai già seguito il corso della nostra partnership.
          </p>
        </div>
      </div>

      {/* Opzione 1: Attestato DTS */}
      <DtsCertificatoUpload user={user} onSuccess={onUnlocked} />

      {/* Opzione 2: Acquista pacchetto */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 shrink-0 rounded-xl bg-[#f59e0b] text-[#0a1020] flex items-center justify-center shadow-md">
            <GraduationCap className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-[#b45309]">
              Opzione ufficiale
            </div>
            <h3 className="mt-0.5 font-extrabold text-[#0a1f55]">Acquista un pacchetto A.C.D.T.</h3>
            <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">
              Tre pacchetti disponibili: <strong>€ 99</strong> (solo corso), <strong>€ 149</strong>
              (corso + tessera annuale), <strong>€ 218,90</strong> (corso + tessera + Registro Nazionale).
            </p>
            <div className="mt-4">
              <a
                href="/#pacchetti"
                data-testid="corso-locked-vai-pacchetti"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-extrabold text-[13.5px] transition-colors"
              >
                <GraduationCap className="h-4 w-4" />
                Vedi pacchetti
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InformativaFormazione({ onAccepted, version }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const accetta = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/formazione/privacy/accetta`, { versione: version }, { withCredentials: true });
      onAccepted();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="formazione-informativa">
      <div className="rounded-2xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-6 md:p-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[11px] font-extrabold uppercase tracking-wider">
          <ShieldCheck className="h-3.5 w-3.5" /> Informativa sulla formazione online
        </div>
        <h2 className="mt-3 text-2xl font-extrabold">Corso A.C.D.T. — Consulente Digitale, Tecnologico e di Sostenibilità</h2>
        <p className="mt-2 text-slate-200 text-[13.5px]">Prima di iniziare il percorso formativo, leggi e accetta l'informativa sul tracciamento della formazione.</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 text-[13.5px] text-slate-700 leading-relaxed">
        <p><strong className="text-[#0a1f55]">Come funziona il tracciamento della formazione:</strong></p>
        <ul className="list-disc pl-5 space-y-2">
          <li>La piattaforma registra l'avvio, la pausa, la ripresa e il completamento dei video formativi, insieme al tempo effettivamente visualizzato.</li>
          <li>Durante le videolezioni ti verrà chiesto di attivare la <strong>videocamera</strong> per la verifica della presenza. Il sistema rileva esclusivamente <strong>VOLTO PRESENTE / VOLTO NON PRESENTE</strong>.</li>
          <li><strong>Nessuna immagine del tuo volto viene registrata o conservata</strong>: viene salvato soltanto il dato presenza = SÌ/NO associato all'intervallo temporale (privacy by design).</li>
          <li>Non viene effettuato alcun riconoscimento biometrico automatizzato, nessuna analisi di emozioni, movimenti o ambiente.</li>
          <li>Per completare ciascun modulo è necessario visualizzare il contenuto obbligatorio e raggiungere almeno l'<strong>80% di presenza</strong>.</li>
          <li>L'Associazione conserva un registro (audit log) delle attività formative per finalità di certificazione della formazione.</li>
          <li>In caso di problemi tecnici puoi sempre richiedere una <strong>verifica manuale</strong> alla segreteria: nessun esito è irreversibile.</li>
        </ul>
        <p className="text-[12.5px] text-slate-500">
          Versione informativa: {version} · Informativa privacy completa: <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#102a6b] font-bold underline">Privacy Policy A.C.D.T.</a>
        </p>
        <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-[#102a6b]/40">
          <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="privacy-formazione-checkbox" className="mt-0.5" />
          <span className="text-[13px]">Dichiaro di aver letto e compreso l'informativa sulla formazione online e sul tracciamento della presenza tramite videocamera, e accetto le regole del percorso formativo A.C.D.T.</span>
        </label>
        <Button
          disabled={!checked || saving}
          onClick={accetta}
          data-testid="privacy-formazione-accetta-btn"
          className="w-full h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accetta e inizia il percorso formativo"}
        </Button>
      </div>
    </div>
  );
}

function Overview({ data, user, onOpenModulo, onRefresh }) {
  const [downloadingAtt, setDownloadingAtt] = useState(false);
  const [busyMod, setBusyMod] = useState(null);
  const [quizModulo, setQuizModulo] = useState(null);
  const corsoCompletato = data.moduli_totali > 0 && data.moduli_completati === data.moduli_totali;
  const { toast } = useToast();

  // Traccia i moduli completati precedenti per rilevare i nuovi sblocchi
  const prevCompletedRef = useRef(null);
  const confettiFiredRef = useRef(false);
  useEffect(() => {
    const completedIds = new Set(
      (data.moduli || []).filter((m) => m.stato === "completato").map((m) => m.id)
    );
    // Prima render: memorizza baseline senza notificare
    if (prevCompletedRef.current === null) {
      prevCompletedRef.current = completedIds;
      // Se già tutto completato al primo caricamento, non sparare i confetti
      confettiFiredRef.current = data.moduli_completati === data.moduli_totali;
      return;
    }
    const prev = prevCompletedRef.current;
    // Trova moduli completati nuovi
    const nuoviCompletati = [...completedIds].filter((id) => !prev.has(id));
    if (nuoviCompletati.length > 0) {
      const tuttoCompletato = data.moduli_completati === data.moduli_totali;
      if (tuttoCompletato && !confettiFiredRef.current) {
        confettiFiredRef.current = true;
        // Confetti + toast dedicato al percorso completato
        fireConfettiCelebration();
        toast({
          title: "🎓 Percorso formativo completato!",
          description: `Hai completato tutti i ${data.moduli_totali} moduli. Scarica il tuo attestato ufficiale.`,
          duration: 10000,
        });
        try {
          window.posthog?.capture?.("formazione_percorso_completato", {
            moduli_totali: data.moduli_totali,
          });
        } catch { /* posthog non disponibile */ }
      } else {
        // Il prossimo modulo sbloccato è il primo non-completato non-bloccato
        const prossimo = (data.moduli || []).find(
          (m) => m.stato !== "completato" && m.stato !== "bloccato"
        );
        if (prossimo) {
          toast({
            title: "🎉 Modulo successivo sbloccato!",
            description: `${prossimo.numero_label} — ${prossimo.titolo}`,
            duration: 6000,
          });
          try {
            window.posthog?.capture?.("formazione_modulo_sbloccato", {
              modulo_id: prossimo.id,
              numero_label: prossimo.numero_label,
              completati: data.moduli_completati,
              totali: data.moduli_totali,
            });
          } catch { /* posthog non disponibile */ }
        }
      }
    }
    prevCompletedRef.current = completedIds;
  }, [data.moduli, data.moduli_completati, data.moduli_totali, toast]);

  const scaricaDispensa = async (m) => {
    setBusyMod(m.id);
    try {
      const r = await axios.post(`${API}/api/formazione/moduli/${m.id}/pdf-download`, {}, { withCredentials: true });
      const dl = await axios.get(`${API}/api/me/corso-abilitazione/materiali/${r.data.materiale_id}/download`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dispensa_${m.numero_label.replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Download non disponibile");
    } finally { setBusyMod(null); }
  };

  const scaricaAttestato = async () => {
    setDownloadingAtt(true);
    try {
      const r = await axios.get(`${API}/api/formazione/attestato.pdf`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "attestato_formazione_acdt.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      alert("Attestato non disponibile. Riprova più tardi.");
    } finally {
      setDownloadingAtt(false);
    }
  };
  const gruppi = [];
  data.moduli.forEach((m) => {
    const g = gruppi.find((x) => x.nome === m.gruppo);
    if (g) g.moduli.push(m); else gruppi.push({ nome: m.gruppo, moduli: [m] });
  });
  return (
    <div className="space-y-6" data-testid="formazione-overview">
      {/* Scheda riepilogativa */}
      <div className="rounded-2xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-[#f59e0b]/25 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[11px] font-extrabold uppercase tracking-wider">
            <GraduationCap className="h-3.5 w-3.5" /> Formazione A.C.D.T.
          </div>
          <h2 className="mt-3 text-xl md:text-2xl font-extrabold leading-tight">{data.corso.titolo}</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-[13px] text-slate-200">
            <div><span className="text-slate-400">Corsista:</span> <strong className="text-white">{data.corsista.nome} {data.corsista.cognome}</strong></div>
            <div><span className="text-slate-400">Tessera:</span> <strong className="text-white">{data.corsista.numero_tessera || "—"}</strong></div>
            <div><span className="text-slate-400">Iscritto al corso:</span> <strong className="text-white">{fmtDate(data.data_iscrizione)}</strong></div>
            <div><span className="text-slate-400">Ultimo accesso:</span> <strong className="text-white">{fmtDate(data.ultimo_accesso)}</strong></div>
          </div>
          <div className="mt-5">
            <div className="flex items-center justify-between text-[12px] font-bold uppercase tracking-wider mb-1.5">
              <span>Avanzamento del corso</span>
              <span className="text-[#fbbf24]" data-testid="formazione-percentuale">{data.percentuale_corso}%</span>
            </div>
            <div className="h-3 rounded-full bg-white/15 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-[#f59e0b] to-[#fbbf24] transition-all" style={{ width: `${data.percentuale_corso}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Attestato — visibile solo a corso completato */}
      {corsoCompletato && (
        <div className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="attestato-banner">
          <div className="h-12 w-12 shrink-0 rounded-xl bg-emerald-500/15 text-emerald-700 flex items-center justify-center">
            <Award className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-extrabold text-[#0a1f55]">🎓 Percorso formativo completato!</h3>
            <p className="text-[13px] text-slate-600 mt-0.5">Hai completato tutti i {data.moduli_totali} moduli con una presenza media del {data.presenza_media}%. Scarica il tuo attestato ufficiale di partecipazione.</p>
          </div>
          <Button onClick={scaricaAttestato} disabled={downloadingAtt} data-testid="scarica-attestato-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            {downloadingAtt ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-2 h-4 w-4" /> Scarica attestato PDF</>}
          </Button>
        </div>
      )}

      {/* Timeline visuale dei moduli */}
      <TimelineFormazione moduli={data.moduli} onOpen={onOpenModulo} />

      {/* Streak formativo */}
      {data.streak && <StreakCard streak={data.streak} />}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: CheckCircle2, l: "Moduli completati", v: `${data.moduli_completati} / ${data.moduli_totali}`, tid: "stat-moduli" },
          { icon: Camera, l: "Presenza media", v: `${data.presenza_media}%`, tid: "stat-presenza" },
          { icon: Clock, l: "Ore completate", v: `${data.ore_completate}h · restano ${data.ore_residue}h`, tid: "stat-ore" },
          { icon: FileText, l: "Materiali disponibili", v: data.materiali_disponibili, tid: "stat-materiali" },
        ].map((s) => (
          <div key={s.l} data-testid={s.tid} className="rounded-2xl border border-slate-200 bg-white p-4">
            <s.icon className="h-5 w-5 text-[#102a6b]" />
            <div className="mt-2 text-[11px] uppercase tracking-wider font-bold text-slate-500">{s.l}</div>
            <div className="text-[15px] font-extrabold text-[#0a1f55]">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Moduli */}
      {gruppi.map((g) => (
        <div key={g.nome} className="space-y-3">
          <h3 className="text-lg font-extrabold text-[#0a1f55] flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[#f59e0b]" /> {g.nome}
          </h3>
          {g.moduli.map((m) => {
            const ui = STATO_UI[m.stato] || STATO_UI.da_iniziare;
            const locked = m.stato === "bloccato";
            return (
              <div
                key={m.id}
                data-testid={`modulo-card-${m.id}`}
                className={`rounded-2xl border bg-white p-5 flex flex-col md:flex-row md:items-center gap-4 transition-all ${locked ? "border-slate-200 opacity-70" : "border-slate-200 hover:border-[#102a6b]/40 hover:shadow-md"}`}
              >
                <div className={`h-12 w-12 shrink-0 rounded-xl flex items-center justify-center ${m.stato === "completato" ? "bg-emerald-500/15 text-emerald-700" : locked ? "bg-slate-100 text-slate-400" : "bg-[#f59e0b]/15 text-[#b45309]"}`}>
                  {m.stato === "completato" ? <CheckCircle2 className="h-5 w-5" /> : locked ? <Lock className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10.5px] uppercase tracking-wider font-extrabold text-slate-500">{m.numero_label}</span>
                    <span className={`text-[10.5px] px-2 py-0.5 rounded-full font-extrabold ${ui.cls}`}>{ui.label}</span>
                  </div>
                  <h4 className="font-bold text-[#0a1f55] mt-0.5">{m.titolo}</h4>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-500">
                    {m.tipo === "dispensa" ? (
                      <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Dispensa PDF da studiare</span>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtMin(m.durata_sec)}</span>
                        <span className="inline-flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Presenza richiesta ≥{m.soglia_presenza}%</span>
                        {m.stato !== "da_iniziare" && m.stato !== "bloccato" && (
                          <span className="inline-flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5" /> Presenza: <strong className="text-[#0a1f55]">{m.presenza_pct}%</strong></span>
                        )}
                      </>
                    )}
                    {m.tipo === "dispensa" && m.pdf_downloaded && m.stato !== "completato" && (
                      <span className="text-emerald-700 font-bold">✓ Dispensa scaricata</span>
                    )}
                    {m.completed_at && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {fmtDate(m.completed_at)}</span>}
                  </div>
                  {m.stato === "in_corso" && m.tipo === "video" && (
                    <div className="mt-2 flex items-center gap-2 max-w-xs">
                      <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-[#f59e0b]" style={{ width: `${m.presenza_pct}%` }} />
                      </div>
                      <span className="text-[11px] font-bold text-[#102a6b]">{m.presenza_pct}%</span>
                    </div>
                  )}
                </div>
                {m.tipo === "dispensa" ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      disabled={locked || busyMod === m.id}
                      onClick={() => scaricaDispensa(m)}
                      data-testid={`dispensa-download-${m.id}`}
                      variant="outline"
                      className="border-slate-300 text-[#102a6b] font-bold"
                    >
                      {busyMod === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "📄 Scarica dispensa"}
                    </Button>
                    {m.stato !== "completato" && (
                      <Button
                        disabled={locked || !m.pdf_downloaded || busyMod === m.id}
                        onClick={() => setQuizModulo(m)}
                        data-testid={`dispensa-quiz-${m.id}`}
                        className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold"
                        title={!m.pdf_downloaded ? "Scarica prima la dispensa" : ""}
                      >
                        {locked ? "Bloccato" : "📝 Verifica di studio (3 domande)"}
                      </Button>
                    )}
                  </div>
                ) : (
                <Button
                  disabled={locked}
                  onClick={() => onOpenModulo(m)}
                  data-testid={`modulo-open-${m.id}`}
                  className={m.stato === "completato" ? "bg-white border border-slate-300 text-[#102a6b] hover:bg-slate-50" : "bg-[#102a6b] hover:bg-[#0a1f55] text-white"}
                >
                  {locked ? "Bloccato" : m.stato === "completato" ? "Rivedi" : m.stato === "in_corso" ? "Continua" : m.ordine === 0 ? "▶️ Avvia Introduzione" : "Avvia"}
                  {!locked && <ChevronRight className="ml-1 h-4 w-4" />}
                </Button>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* ESAME FINALE — sbloccato al completamento di tutti i moduli */}
      {(corsoCompletato || user?.esame_abilitazione_superato) ? (
        <div className="space-y-3" data-testid="esame-finale-unificato">
          <h3 className="text-lg font-extrabold text-[#0a1f55] flex items-center gap-2">
            <Award className="h-5 w-5 text-[#f59e0b]" /> Esame Finale
          </h3>
          <EsameFinaleSection user={user} hasCorsoAccess={true} />
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4 opacity-80" data-testid="esame-finale-bloccato">
          <div className="h-12 w-12 shrink-0 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center"><Lock className="h-5 w-5" /></div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-wider font-extrabold text-slate-500">Fase finale</span>
              <span className="text-[10.5px] px-2 py-0.5 rounded-full font-extrabold bg-slate-100 text-slate-500">🔒 Bloccato</span>
            </div>
            <h4 className="font-bold text-[#0a1f55] mt-0.5">🎓 Esame Finale di Qualificazione</h4>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Completa tutti i moduli del percorso ({data.moduli_completati}/{data.moduli_totali}) per sbloccare l'esame in formato ministeriale e ottenere il certificato.
            </p>
          </div>
        </div>
      )}

      {quizModulo && (
        <QuizDialog modulo={quizModulo} onClose={(passed) => { setQuizModulo(null); if (passed) onRefresh(); }} />
      )}
    </div>
  );
}

function QuizDialog({ modulo, onClose }) {
  const [domande, setDomande] = useState(null);
  const [risposte, setRisposte] = useState({});
  const [esito, setEsito] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/formazione/moduli/${modulo.id}/quiz`, { withCredentials: true })
      .then((r) => setDomande(r.data.domande))
      .catch((e) => { alert(e.response?.data?.detail || "Quiz non disponibile"); onClose(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modulo.id]);

  const invia = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/formazione/moduli/${modulo.id}/quiz`,
        { risposte: domande.map((_, i) => risposte[i]) }, { withCredentials: true });
      setEsito(r.data);
      if (r.data.superato) setTimeout(() => onClose(true), 1800);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore invio");
    } finally { setSending(false); }
  };

  const tutte = domande && domande.every((_, i) => risposte[i] !== undefined);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(esito?.superato); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="quiz-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0a1f55]">📝 Verifica di studio — {modulo.numero_label}</DialogTitle>
        </DialogHeader>
        {!domande ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#102a6b]" /></div>
        ) : (
          <div className="space-y-5">
            <p className="text-[12.5px] text-slate-500">Rispondi correttamente a tutte le 3 domande per completare il modulo e sbloccare il successivo.</p>
            {domande.map((d, i) => (
              <div key={i} className="space-y-2">
                <div className="font-bold text-[13.5px] text-[#0a1f55]">{i + 1}. {d.q}</div>
                {d.opzioni.map((opt, j) => (
                  <label key={j} className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer text-[13px] transition-colors ${risposte[i] === j ? "border-[#102a6b] bg-[#102a6b]/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <input type="radio" name={`q${i}`} checked={risposte[i] === j} onChange={() => setRisposte({ ...risposte, [i]: j })} data-testid={`quiz-q${i}-opt${j}`} className="mt-0.5" />
                    {opt}
                  </label>
                ))}
              </div>
            ))}
            {esito && !esito.superato && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[13px] text-amber-800 font-bold" data-testid="quiz-esito-fallito">
                ⚠️ {esito.corrette}/{esito.totale} risposte corrette — rileggi la dispensa e riprova.
              </div>
            )}
            {esito && esito.superato ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-[13px] text-emerald-700 font-bold" data-testid="quiz-esito-superato">
                ✅ Verifica superata! Modulo completato — il successivo è sbloccato.
              </div>
            ) : (
              <Button onClick={invia} disabled={!tutte || sending} data-testid="quiz-invia-btn" className="w-full bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia risposte"}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
